import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { promptErrorMessage } from '@twasta/page-runtime/userMemory'
import {
  listUserMemory, saveUserMemory, forgetUserMemory, notifySecurityContextChanged,
  type MemorySurface, type PromptVariable, type PromptValueIn,
} from '@/api/userMemory'

interface Props {
  projectId: string
  surface?: MemorySurface
  onClose: () => void
  /** Override the data layer (e.g. the site-auth endpoints). */
  loader?: () => Promise<PromptVariable[]>
  saver?: (values: PromptValueIn[]) => Promise<void>
  forgetter?: (varName: string) => Promise<void>
}

/**
 * "Project settings" dialog reached from the user menu. Lists the project's
 * prompt variables with the user's current value, lets them change it, toggle
 * "Remember this", and forget a remembered value (re-asked next session).
 */
export default function ProjectSettingsModal({
  projectId, surface = 'internal', onClose, loader, saver, forgetter,
}: Props) {
  const [loading, setLoading] = useState(true)
  const [vars, setVars] = useState<PromptVariable[]>([])
  const [values, setValues] = useState<Record<string, string>>({})
  const [remember, setRemember] = useState<Record<string, boolean>>({})
  // Per-variable "type a free value" escape for select vars whose master/source
  // isn't created yet (empty/unreadable dropdown) — avoids the import deadlock.
  const [typed, setTyped] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const list = await (loader ? loader() : listUserMemory(projectId, surface))
      setVars(list)
      const v: Record<string, string> = {}
      const r: Record<string, boolean> = {}
      const t: Record<string, boolean> = {}
      for (const item of list) {
        v[item.name] = (item.value ?? item.default ?? '') as string
        r[item.name] = item.remember ?? item.remember_default ?? true
        // Free text only where the list cannot offer anything — and never for
        // the security context, whose value decides the user's rights.
        if (item.input_mode === 'select' && !item.security_context) {
          const opts = item.options || []
          t[item.name] = !!item.source_missing || opts.length === 0
        }
      }
      setValues(v)
      setRemember(r)
      setTyped(t)
    } catch {
      toast.error('Failed to load project settings')
    } finally {
      setLoading(false)
    }
  }, [projectId, surface])

  useEffect(() => { refresh() }, [refresh])

  const save = async () => {
    setSaving(true)
    try {
      const payload = vars.map((v) => ({
        var_name: v.name,
        value: values[v.name] ?? '',
        remember: v.security_context ? v.remember_default : !!remember[v.name],
      }))
      await (saver ? saver(payload) : saveUserMemory(projectId, payload))
      // Switching the security context (e.g. financial entity) changes which
      // roles apply — restart the app under the new one.
      const switched = vars.some((v) => v.security_context
        && String(values[v.name] ?? '') !== String(v.value ?? ''))
      if (switched) {
        toast.success('Switched — reloading with your rights there')
        notifySecurityContextChanged()
        return
      }
      toast.success('Saved')
      onClose()
    } catch (err) {
      // The server names the refusal ("You have no rights in 'FE2'").
      toast.error(promptErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const forget = async (name: string) => {
    try {
      await (forgetter ? forgetter(name) : forgetUserMemory(projectId, name))
      toast.success('Will be asked again next time')
      refresh()
    } catch {
      toast.error('Failed to update')
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 20px 6px' }}>
          <h2 style={{ margin: 0, fontSize: '1.1625rem', fontWeight: 700 }}>Project settings</h2>
          <p style={{ margin: '6px 0 0', fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
            Values the app asks you for. Update them here any time.
          </p>
        </div>

        <div style={body}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" /></div>
          ) : vars.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: '0.9125rem' }}>
              This app has no user-specific settings.
            </div>
          ) : vars.map((v) => (
            <div key={v.name} style={field}>
              <label style={{ fontSize: '0.9125rem', fontWeight: 600 }}>{v.prompt || v.name}</label>
              {v.security_context ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  {(v.options || []).length === 0
                    ? 'You have no rights in any of these. Ask your administrator to assign you a role.'
                    : 'What you may do in the app depends on this. Switching reloads the app.'}
                </div>
              ) : null}
              {v.input_mode === 'select' && !v.security_context
                && (v.source_missing || (v.options || []).length === 0) ? (
                <div style={notice}>
                  This list isn’t available yet — its source data{' '}
                  {v.source_missing ? 'hasn’t been created' : 'has no entries'} for this
                  project. Type a value below, then add the same value to its master/source
                  data so it shows up in the list next time.
                </div>
              ) : null}
              {v.input_mode === 'select' && !typed[v.name] ? (
                <select
                  style={input}
                  value={values[v.name] ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '__twasta_type_value__') {
                      setTyped((s) => ({ ...s, [v.name]: true })); return
                    }
                    setValues((s) => ({ ...s, [v.name]: e.target.value }))
                  }}
                >
                  <option value="">— Select —</option>
                  {(v.options || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label || o.value}</option>
                  ))}
                  {!v.security_context && ((v.options || []).length === 0 || v.source_missing) ? (
                    <option value="__twasta_type_value__">✎ Type a value…</option>
                  ) : null}
                </select>
              ) : (
                <>
                  <input
                    style={input}
                    value={values[v.name] ?? ''}
                    onChange={(e) => setValues((s) => ({ ...s, [v.name]: e.target.value }))}
                  />
                  {v.input_mode === 'select' && (v.options || []).length > 0 ? (
                    <button type="button" style={chooseListBtn}
                      onClick={() => setTyped((s) => ({ ...s, [v.name]: false }))}>
                      ↩ Choose from list
                    </button>
                  ) : null}
                </>
              )}
              {v.security_context ? null : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={rememberRow}>
                  <input
                    type="checkbox"
                    checked={!!remember[v.name]}
                    onChange={(e) => setRemember((s) => ({ ...s, [v.name]: e.target.checked }))}
                  />
                  Remember this
                </label>
                {v.remember && (
                  <button type="button" style={linkBtn} onClick={() => forget(v.name)}>
                    Forget saved value
                  </button>
                )}
              </div>
              )}
            </div>
          ))}
        </div>

        <div style={footer}>
          <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>Close</button>
          <button type="button" style={btnPrimary} onClick={save} disabled={saving || loading || vars.length === 0}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}
const card: React.CSSProperties = {
  width: 'min(520px, 96vw)', maxHeight: '90vh', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
  borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
}
const body: React.CSSProperties = {
  padding: '12px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14,
}
const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5 }
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border, #d1d5db)',
  background: 'var(--lp-input-bg, #fff)', color: 'var(--color-text, #111827)',
  fontSize: '0.9625rem', outline: 'none', width: '100%', boxSizing: 'border-box',
}
const notice: React.CSSProperties = {
  fontSize: '0.8125rem', lineHeight: 1.45, padding: '7px 9px', borderRadius: 6,
  background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.45)',
  color: 'var(--color-text, #92400e)',
}
const chooseListBtn: React.CSSProperties = {
  alignSelf: 'flex-start', padding: 0, marginTop: 2, background: 'none',
  border: 'none', cursor: 'pointer', color: 'var(--color-primary, #2563eb)',
  fontSize: '0.8125rem',
}
const rememberRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem',
  color: 'var(--color-text-secondary, #6b7280)', cursor: 'pointer',
}
const linkBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: '#ef4444',
  fontSize: '0.8425rem', cursor: 'pointer', padding: 0,
}
const footer: React.CSSProperties = {
  padding: '12px 20px 18px', display: 'flex', justifyContent: 'flex-end',
  alignItems: 'center', gap: 8, borderTop: '1px solid var(--color-border, #e5e7eb)',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: 'var(--color-primary, #2563eb)', color: '#fff', fontSize: '0.9625rem', fontWeight: 600,
}
const btnSecondary: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--color-border, #d1d5db)', background: 'transparent',
  color: 'var(--color-text, #111827)', fontSize: '0.9625rem',
}
